import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

// Import modals
import ReviewRequestModal from '../modals/ReviewRequestModal';
import RejectRequestModal from '../modals/RejectRequestModal';

const DocumentRequestsManagement = () => {
  const routes = all_routes;
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  // Mock requests data
  const mockRequests = [
    {
      id: 1,
      requestId: "REQ-2024-001",
      studentName: "John Smith",
      studentId: "STU-2024-123",
      documentType: "Transfer Certificate",
      requestDate: "2024-12-20",
      status: "Pending",
      priority: "High",
      expectedDate: "2024-12-27"
    },
    {
      id: 2,
      requestId: "REQ-2024-002", 
      studentName: "Sarah Johnson",
      studentId: "STU-2024-456",
      documentType: "Character Certificate",
      requestDate: "2024-12-18",
      status: "In Progress",
      priority: "Medium",
      expectedDate: "2024-12-25"
    },
    {
      id: 3,
      requestId: "REQ-2024-003",
      studentName: "Mike Wilson",
      studentId: "STU-2024-789",
      documentType: "Grade Report",
      requestDate: "2024-12-15",
      status: "Approved",
      priority: "Low", 
      expectedDate: "2024-12-22"
    }
  ];

  const filteredRequests = mockRequests.filter(request => {
    if (filter !== 'all') return request.status.toLowerCase() === filter;
    return true;
  });

  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'pending': return 'badge-soft-warning';
      case 'in progress': return 'badge-soft-info';
      case 'approved': return 'badge-soft-success';
      case 'rejected': return 'badge-soft-danger';
      default: return 'badge-soft-secondary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority.toLowerCase()) {
      case 'high': return 'badge-soft-danger';
      case 'medium': return 'badge-soft-warning';
      case 'low': return 'badge-soft-success';
      default: return 'badge-soft-secondary';
    }
  };

  const getStatsCount = (status: string) => {
    return mockRequests.filter(req => req.status === status).length;
  };

  const handleReviewRequest = (request: any) => {
    setSelectedRequest(request);
  };

  const handleRejectRequest = (request: any) => {
    setSelectedRequest(request);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Document Requests Management</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Admin Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Document Requests Management
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="pe-1 mb-2">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-top">Refresh</Tooltip>}
                >
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white btn-icon me-1"
                  >
                    <i className="ti ti-refresh" />
                  </Link>
                </OverlayTrigger>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row mt-4">
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-warning-transparent me-3">
                      <i className="ti ti-clock"></i>
                    </div>
                    <div>
                      <h4>{getStatsCount('Pending')}</h4>
                      <p className="text-muted mb-0">Pending</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-info-transparent me-3">
                      <i className="ti ti-progress"></i>
                    </div>
                    <div>
                      <h4>{getStatsCount('In Progress')}</h4>
                      <p className="text-muted mb-0">In Progress</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-success-transparent me-3">
                      <i className="ti ti-check"></i>
                    </div>
                    <div>
                      <h4>{getStatsCount('Approved')}</h4>
                      <p className="text-muted mb-0">Approved</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-danger-transparent me-3">
                      <i className="ti ti-x"></i>
                    </div>
                    <div>
                      <h4>{getStatsCount('Rejected')}</h4>
                      <p className="text-muted mb-0">Rejected</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">Document Requests Management</h4>
                  <p className="text-muted">Manage teachers, students, parents, and staff accounts.</p>
                </div>

                {/* Filters */}
                <div className="card-body border-bottom">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Search Requests</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="ti ti-search"></i></span>
                        <input type="text" className="form-control" placeholder="Search by student name, ID..." />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Filter by Status</label>
                      <select 
                        className="form-select"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="in progress">In Progress</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Filter by Type</label>
                      <select className="form-select">
                        <option>All Types</option>
                        <option>Transfer Certificate</option>
                        <option>Character Certificate</option>
                        <option>Grade Report</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">&nbsp;</label>
                      <button className="btn btn-outline-secondary w-100">
                        <i className="ti ti-filter me-1"></i>Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* TODO Placeholders */}
                <div className="card-body border-bottom">
                  <div className="alert alert-info">
                    <h6><i className="ti ti-info-circle me-2"></i>TODO: Connect to document requests API</h6>
                    <ul className="mb-0">
                      <li>Fetch document requests from database</li>
                      <li>Add/Edit/Delete document requests</li>
                      <li>Role assignment functionality</li>
                      <li>Approve/Reject requests workflow</li>
                      <li>Generate reports and notifications</li>
                    </ul>
                  </div>
                </div>

                {/* Requests Table */}
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Request ID</th>
                          <th>Student Info</th>
                          <th>Document Type</th>
                          <th>Request Date</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Expected Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRequests.map((request) => (
                          <tr key={request.id}>
                            <td>
                              <Link to="#" className="text-primary fw-semibold">
                                {request.requestId}
                              </Link>
                            </td>
                            <td>
                              <div>
                                <h6 className="mb-0">{request.studentName}</h6>
                                <small className="text-muted">{request.studentId}</small>
                              </div>
                            </td>
                            <td>{request.documentType}</td>
                            <td>{request.requestDate}</td>
                            <td>
                              <span className={`badge ${getStatusColor(request.status)}`}>
                                {request.status}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${getPriorityColor(request.priority)}`}>
                                {request.priority}
                              </span>
                            </td>
                            <td>{request.expectedDate}</td>
                            <td>
                              <div className="dropdown">
                                <button className="btn btn-white btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                                  Actions
                                </button>
                                <ul className="dropdown-menu">
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-eye me-2"></i>View Details
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item text-success"
                                      disabled={request.status === 'Approved'}
                                      data-bs-toggle="modal"
                                      data-bs-target="#reviewRequestModal"
                                      onClick={() => handleReviewRequest(request)}
                                    >
                                      <i className="ti ti-check me-2"></i>Review
                                    </button>
                                  </li>
                                  <li>
                                    <button 
                                      className="dropdown-item text-danger"
                                      disabled={request.status === 'Rejected'}
                                      data-bs-toggle="modal"
                                      data-bs-target="#rejectRequestModal"
                                      onClick={() => handleRejectRequest(request)}
                                    >
                                      <i className="ti ti-x me-2"></i>Reject
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-edit me-2"></i>Edit
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-download me-2"></i>Download
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <p className="text-muted mb-0">Showing {filteredRequests.length} of {mockRequests.length} requests</p>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className="page-item disabled">
                          <button className="page-link">Previous</button>
                        </li>
                        <li className="page-item active">
                          <button className="page-link">1</button>
                        </li>
                        <li className="page-item">
                          <button className="page-link">2</button>
                        </li>
                        <li className="page-item">
                          <button className="page-link">Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ReviewRequestModal requestId={selectedRequest?.requestId} />
      <RejectRequestModal requestId={selectedRequest?.requestId} />
    </>
  );
};

export default DocumentRequestsManagement;
